//@ts-check
import "common/components/custom-range-slider";
import { h } from "common/dom-builder";
import { getFontVariations } from "../font-parsing/font-parsing";

export class FontVariationSelector {
  element;
  axes = [];
  values = {};

  /**
   * @param {{typeTester: import("../TypeTester").TypeTester, element: HTMLElement}} props
   */
  constructor(props) {
    this.element = props.element;
    this.props = props;
    this.init();
  }

  get typeTester() {
    return this.props.typeTester;
  }

  get fontStyle() {
    return this.props.typeTester.props.fontStyle;
  }

  /**
   * @param {import("../TypeTester").TypeTester} typeTester
   * @returns
   */
  static buildElementForHeader(typeTester) {
    if (!typeTester.isFormattable) return;

    const element = h(
      "div",
      {
        class: ["input-group", "variation-input-group", "in-header"],
        "data-input-group": "variation",
      },
      []
    );
    const instance = new FontVariationSelector({ typeTester, element });
    return { element, instance };
  }

  async init() {
    await this.loadAxes();
    this.render();
    this.typeTester.addEventListener("reset", () => {
      this.resetValues();
      this.applySettings();
      this.updateInputs();
    });
  }

  async loadAxes() {
    // Prefer metadata.axes from backend (extracted at upload) to avoid loading font client-side
    if (this.fontStyle.metadata?.axes?.length) {
      this.axes = this.fontStyle.metadata.axes;
    } else {
      try {
        this.axes = await this.loadAxesFromFont(
          this.fontStyle.name,
          this.fontStyle.woff2FileUrl || this.fontStyle.woffFileUrl
        );
      } catch (error) {
        console.error("Failed to load font variations", error);
        this.axes = [];
      }
    }
    this.initValues();
    this.applySettings();
    this.dispatchValuesChanged();
  }

  async loadAxesFromFont(name, src) {
    const ext = src?.split(".").pop()?.split("?")[0];
    return getFontVariations({ name, src, ext });
  }

  /** Set values from saved DB state, falling back to axis defaults. */
  initValues() {
    const savedAxisValues = this.typeTester.props.format?.axisValues;
    this.axes.forEach((axis) => {
      this.values[axis.tag] =
        savedAxisValues?.[axis.tag] ?? axis.default ?? axis.min;
    });
  }

  /** Reset axes to saved DB values, falling back to font-defined defaults. */
  resetValues() {
    const savedAxisValues = this.typeTester.props.format?.axisValues;
    this.axes.forEach((axis) => {
      this.values[axis.tag] =
        savedAxisValues?.[axis.tag] ?? axis.default ?? axis.min;
    });
  }

  /** Returns current axis values as a plain object. */
  getValues() {
    return { ...this.values };
  }

  applySettings() {
    const settings = this.axes
      .map((axis) => `"${axis.tag}" ${this.values[axis.tag]}`)
      .join(", ");
    this.typeTester.textEditor.setFontVariationSettings(settings);
  }

  updateInputs() {
    this.element.querySelectorAll("custom-range-slider").forEach(
      /**
       * @param {HTMLInputElement} input
       */
      (input) => {
        const tag = input.dataset.axisTag;
        if (!tag) return;

        input.value = this.values[tag];

        const axis = this.axes.find((axis) => axis.tag === tag);
        if (axis) this.updateValueIndicator(input, axis);
      }
    );
  }

  render() {
    if (this.axes.length === 0) {
      return;
    }

    this.element.appendChild(
      h(
        "div",
        { class: "items", style: `--number-of-axes: ${this.axes.length}` },
        this.axes.map((axis) => this.buildItem(axis))
      )
    );
  }

  buildItem(axis) {
    return h(
      "div",
      {
        class: [
          "control",
          "variation-control",
          "with-icon-in-front",
          "with-value-indicator",
          "tool-panel",
        ],
      },
      [
        h("span", { class: "axis-label" }, axis.name || axis.tag),
        this.buildInput(axis),
        h(
          "span",
          {
            class: "value-indicator tabular-numbers",
            style: `--value-indicator-max-digits: ${Math.max(
              Math.ceil(axis.max).toString().length,
              Math.floor(axis.min).toString().length
            )};`,
          },
          [`${this.values[axis.tag]}`]
        ),
      ]
    );
  }

  buildInput(axis) {
    const inputElement = h("custom-range-slider", {
      min: axis.min,
      max: axis.max,
      step: 1,
      value: this.values[axis.tag] ?? axis.default ?? axis.min,
      "data-axis-tag": axis.tag,
    });
    inputElement.addEventListener("input", (ev) => {
      const target = /** @type {HTMLInputElement} */ (ev.target);
      if (!target) return;
      this.values[axis.tag] = parseFloat(target.value);
      this.applySettings();
      this.updateValueIndicator(inputElement, axis);
      this.dispatchValuesChanged();
    });
    inputElement.addEventListener("change", () => {
      this.dispatchValuesChanged();
      this.typeTester.element.dispatchEvent(new CustomEvent("interaction"));
    });
    return inputElement;
  }

  /** Notify listeners (e.g. admin form) that axis values changed. */
  dispatchValuesChanged() {
    this.typeTester.element.dispatchEvent(
      new CustomEvent("axis-values-change", {
        detail: { values: this.getValues() },
      })
    );
  }

  updateValueIndicator(inputElement, axis) {
    const indicator = inputElement.nextElementSibling;
    if (!indicator) return;
    const value = this.values[axis.tag];
    const paddedValue = value.toString().padStart(3, " ");
    indicator.textContent = `${paddedValue}`;
  }
}
